import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { createBrowserRouter, RouterProvider } from 'react-router-dom'
import { FLEET_PORTAL_ROLES, type FleetSummary, type UserTaskSummary } from '@autoplan/common'
import { I18nProvider } from '@_shared/context/I18nProvider'
import { UserProvider } from '@_shared/context/UserProvider'
import Layout from './Layout'
import { Home } from './Views/Home'
import { Articles } from './Views/Articles'
import { Article } from './Views/Article'
import { Help } from './Views/Help'
import { PreviewShell } from './preview-shell'
import './index.css'

const router = createBrowserRouter([
  {
    element: <PreviewShell />,
    children: [
      {
        path: '/',
        element: <Layout withoutSidebarPadding />,
        children: [
          { index: true, element: <Home /> },
          { path: 'articles', element: <Articles /> },
          { path: 'articles/:slug', element: <Article /> },
          { path: 'help', element: <Help /> },
        ],
      },
    ],
  },
])

// The preview has no signed-in user or portal API, so the parts of the home page that come from
// the API (summary boxes, my tasks) are shown with example data instead of being hidden.
const PREVIEW_FLEET_SUMMARY: FleetSummary = {
  totalCount: 1200,
  orderedCount: 40,
  activeCount: 950,
  expiringCount: 397,
  closedCount: 210,
  unresolvedCount: 393,
  administratedCount: 600,
  overMileageCount: 169,
}

const PREVIEW_USER_SUMMARY: UserTaskSummary = { readyForSigning: 4, sentForSigning: 2, selections: 1 }

// Unsigned token that only carries the admin role, so permission checks show every section.
// It is never sent anywhere: the queries below are pre-filled and never refetch.
const toBase64Url = (value: object) => btoa(JSON.stringify(value)).replace(/=+$/, '')
const PREVIEW_USER = {
  access_token: `${toBase64Url({ alg: 'none' })}.${toBase64Url({ groupIds: [FLEET_PORTAL_ROLES.ADMIN] })}.`,
}

// updatedAt in the far future keeps the data fresh despite the hooks' own staleTime, so the API is never called
const NEVER_STALE = { updatedAt: Number.MAX_SAFE_INTEGER }
const queryClient = new QueryClient()
queryClient.setQueryData(['fleet-summary'], PREVIEW_FLEET_SUMMARY, NEVER_STALE)
queryClient.setQueryData(['user-summary'], PREVIEW_USER_SUMMARY, NEVER_STALE)

createRoot(document.getElementById('root')!).render(
  <QueryClientProvider client={queryClient}>
    <I18nProvider namespace="home">
      <UserProvider user={PREVIEW_USER}>
        <RouterProvider router={router} />
      </UserProvider>
    </I18nProvider>
  </QueryClientProvider>,
)
